import React, { useEffect, useState, useMemo } from "react";
import {
  Grid,
  Card,
  Typography,
  Chip,
  Button,
  Box,
  Pagination,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import { fetchRequestTypes } from "./Utils/archRequest.api";

const ITEMS_PER_PAGE = 12;

const ArchitecturalRequestsCards = ({ requests = [], getStatusDetails }) => {
  const [requestTypes, setRequestTypes] = useState([]);
  const [page, setPage] = useState(1);
  const [loadingTypes, setLoadingTypes] = useState(true);

  useEffect(() => {
    const loadRequestTypes = async () => {
      try {
        setLoadingTypes(true);
        const types = await fetchRequestTypes();
        setRequestTypes(types || []);
      } catch (error) {
        console.error("Error fetching request types:", error);
        setRequestTypes([]);
      } finally {
        setLoadingTypes(false);
      }
    };

    loadRequestTypes();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [requests]);

  const getRequestTypeName = (typeId) => {
    const found = requestTypes.find((t) => Number(t.id) === Number(typeId));
    return found?.type || "Unknown Type";
  };

  const paginated = useMemo(() => {
    return requests.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  }, [requests, page]);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  if (!Array.isArray(requests)) return null;

  if (loadingTypes) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (requests.length === 0) {
    return (
      <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No architectural requests found
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
          There are no requests matching the current filters.
        </Typography>
      </Card>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2.25}>
        {paginated.map((r) => {
          const { color, Icon, label } = getStatusDetails(r.status || "");
          const requestTypeName = getRequestTypeName(r.request_type);

          return (
            <Grid item xs={12} md={8} xl={4} key={r.id}>
              <Card
                sx={{
                  ...pageCardSx,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderLeft: (theme) =>
                    `6px solid ${theme.palette[color]?.main || color}`,
                  p: 2.25,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      gap: 1.5,
                      mb: 1.5,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(47,99,173,0.08)",
                          flexShrink: 0,
                        }}
                      >
                        <HomeWorkIcon sx={{ color: "#2f63ad" }} />
                      </Box>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: "#223142",
                            lineHeight: 1.2,
                          }}
                          noWrap
                        >
                          REQ-{r.id}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ color: "#6b778c", mt: 0.25 }}
                          noWrap
                        >
                          {requestTypeName}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        flexShrink: 0,
                        width: { xs: "100%", sm: "auto" },
                        display: "flex",
                        justifyContent: { xs: "flex-start", sm: "flex-end" },
                      }}
                    >
                      <Chip
                        icon={
                          Icon ? (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Icon sx={{ color: "#fff !important", fontSize: 18 }} />
                            </Box>
                          ) : null
                        }
                        label={String(label || r.status || "Unknown").toUpperCase()}
                        sx={{
                          backgroundColor: (theme) =>
                            theme.palette[color]?.main || color,
                          color: "#fff",
                          fontWeight: 700,
                          height: 32,
                          borderRadius: "999px",
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            px: 1,
                          },
                          "& .MuiChip-icon": {
                            color: "#fff",
                            ml: 0.75,
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <Stack spacing={0.9} sx={{ mb: 2, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: "#223142" }}
                      >
                        Property ID:
                      </Box>{" "}
                      {r.property_id || "N/A"}
                    </Typography>

                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: "#223142" }}
                      >
                        Requested:
                      </Box>{" "}
                      {r.requested_at
                        ? new Date(r.requested_at).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      mt: "auto",
                      p: 1.5,
                      borderRadius: "14px",
                      backgroundColor: "rgba(47,99,173,0.03)",
                      border: "1px solid rgba(47,99,173,0.08)",
                      mb: 2,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#223142", fontWeight: 700 }}
                      noWrap
                    >
                      {r.fullname || "No Name"}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: "#6b778c", mt: 0.4 }}
                      noWrap
                    >
                      {r.phone || "No Phone"}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b778c",
                        mt: 0.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      {r.full_address || "No Address"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
                    <Button
                      component={Link}
                      to={`${r.id}`}
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      sx={secondaryButtonSx}
                    >
                      View Details
                    </Button>
                  </Box>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {requests.length > ITEMS_PER_PAGE && (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
            {Math.min(page * ITEMS_PER_PAGE, requests.length)} of {requests.length} requests
          </Typography>

          <Pagination
            count={Math.ceil(requests.length / ITEMS_PER_PAGE)}
            page={page}
            onChange={(_, val) => setPage(val)}
            color="primary"
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                fontWeight: 600,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ArchitecturalRequestsCards;

